import { BULLET_RADIUS, BULLET_LIFETIME_TICKS, SHIP_NOSE_OFFSET, BULLET_SPEED } from '../constants.js';
import { rotatePoint } from '../helpers.js';
import { wrap } from '../physics.js';

/**
 * Post–ship-update nose position and slug velocity (same math as {@link GameWorld.spawnBulletFromShip}).
 * @param {{ position: { x: number, y: number }, velocity: { x: number, y: number }, rotation: number }} ship
 */
export function bulletSpawnKinematicsFromShip(ship) {
  const offset = rotatePoint(
    { x: 0, y: -SHIP_NOSE_OFFSET },
    { x: 0, y: 0 },
    ship.rotation * Math.PI / 180
  );
  const speedUnit = BULLET_SPEED / SHIP_NOSE_OFFSET;
  return {
    x: ship.position.x + offset.x,
    y: ship.position.y + offset.y,
    vx: ship.velocity.x + offset.x * speedUnit,
    vy: ship.velocity.y + offset.y * speedUnit,
    rotation: ship.rotation
  }
}

/**
 * Linear-motion projectile. Wraps with the world and expires after a short
 * lifespan so missed shots do not orbit forever.
 */
export default class Bullet {
  constructor({ id, ownerId, position, velocity, rotation, fireInputSeq }) {
    this.id = id;
    this.ownerId = ownerId;
    this.position = { x: position.x, y: position.y };
    this.velocity = { x: velocity.x, y: velocity.y };
    this.rotation = rotation;
    // Server: input `seq` of the command consumed the tick this bullet was fired (optional).
    this.fireInputSeq = fireInputSeq;
    this.radius = BULLET_RADIUS;
    this.ageTicks = 0;
    this.dead = false;
    this.deadReason = null;
  }

  update(dt, worldBounds) {
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;
    this.ageTicks += dt;
    if (worldBounds.toroidalMap === false) {
      let hitWall = false;
      if (this.position.x < this.radius) {
        this.position.x = this.radius;
        hitWall = true;
      } else if (this.position.x > worldBounds.width - this.radius) {
        this.position.x = worldBounds.width - this.radius;
        hitWall = true;
      }
      if (this.position.y < this.radius) {
        this.position.y = this.radius;
        hitWall = true;
      } else if (this.position.y > worldBounds.height - this.radius) {
        this.position.y = worldBounds.height - this.radius;
        hitWall = true;
      }
      if (hitWall) {
        this.dead = true;
        this.deadReason = 'wall';
      }
    } else {
      wrap(this.position, worldBounds.width, worldBounds.height, this.radius);
    }
    if (this.ageTicks >= BULLET_LIFETIME_TICKS) {
      this.dead = true;
      this.deadReason = 'ttl';
    }
  }
}
